import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';

const { FiX, FiBriefcase, FiMail, FiShoppingBag, FiFileText, FiCheck, FiStar, FiAlertTriangle } = FiIcons;

const MerchantApplicationModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess]       = useState(false);
  const [error, setError]               = useState('');
  const [formData, setFormData]         = useState({
    companyName: '',
    email: '',
    shopName: '',
    description: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const session = getSession();
      const payload = {
        id:           crypto.randomUUID(),
        company_name: formData.companyName.trim(),
        email:        formData.email.trim().toLowerCase(),
        shop_name:    formData.shopName.trim(),
        description:  formData.description.trim(),
        status:       'pending',
        owner_id:     session?.id || 'anonymous',
      };

      const res  = await writeFetch('create/merchant_applications_1741165000', {
        method: 'POST',
        body:   JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === 'success') {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          onClose();
          setFormData({ companyName: '', email: '', shopName: '', description: '' });
        }, 3500);
      } else {
        setError(data.message || 'Submission failed. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }

    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto sm:items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[32px] md:rounded-[40px] shadow-2xl w-full max-w-2xl my-4 sm:my-8 overflow-hidden border border-brand-dark"
      >
        {/* Header */}
        <div className="p-6 md:p-10 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-accent-navy text-white rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiStar} className="w-5 h-5 md:w-6 md:h-6 text-accent-gold" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-serif text-accent-navy">Merchant Application</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Join our curated marketplace</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-10">
          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.form
                key="merchant-form"
                onSubmit={handleSubmit}
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Company Name</label>
                    <div className="relative group">
                      <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold transition-colors" />
                      <input
                        required
                        className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all"
                        placeholder="e.g. Legacy Crafts Ltd."
                        value={formData.companyName}
                        onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Primary Contact Email</label>
                    <div className="relative group">
                      <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold transition-colors" />
                      <input
                        required
                        type="email"
                        className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all"
                        placeholder="contact@yourbusiness.com"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Proposed Shop Name</label>
                  <div className="relative group">
                    <SafeIcon icon={FiShoppingBag} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold transition-colors" />
                    <input
                      required
                      className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all"
                      placeholder="e.g. The Remembrance Studio"
                      value={formData.shopName}
                      onChange={e => setFormData({ ...formData, shopName: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Products & Services Description</label>
                  <div className="relative group">
                    <SafeIcon icon={FiFileText} className="absolute left-4 top-5 text-text-muted group-focus-within:text-accent-gold transition-colors" />
                    <textarea
                      required
                      className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none h-32 resize-none text-sm leading-relaxed transition-all"
                      placeholder="Describe the memorial items or services you plan to offer…"
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-5 text-lg flex items-center justify-center gap-3 shadow-xl transform active:scale-95 transition-all disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Submit Application <SafeIcon icon={FiCheck} className="w-5 h-5" /></>
                  )}
                </button>

                <p className="text-[10px] text-center text-text-muted font-bold uppercase tracking-wider">
                  Applications are typically reviewed within 3–5 business days
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="application-success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-12 text-center"
              >
                <div className="w-24 h-24 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                  <SafeIcon icon={FiCheck} className="w-12 h-12" />
                </div>
                <h4 className="text-3xl font-serif text-accent-navy mb-4">Application Submitted!</h4>
                <p className="text-sm text-text-muted font-light leading-relaxed mb-8 max-w-sm mx-auto">
                  Thank you for your interest in joining the OneFinalLegacy marketplace. Our curation team will review your application and reach out shortly.
                </p>
                <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark flex items-center gap-4 text-left max-w-md mx-auto">
                  <SafeIcon icon={FiMail} className="text-accent-gold w-6 h-6 flex-shrink-0" />
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    Upon approval you will receive a <span className="font-bold text-accent-navy">6-digit access code</span> and a direct portal sign-in link at{' '}
                    <span className="font-bold text-accent-navy">{formData.email}</span>.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default MerchantApplicationModal;